import * as THREE from 'three';
import React, { useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import ComponentBox from './components/ComponentBox';
import CurrentWire from './components/CurrentWire';
import { log } from 'three/src/nodes/TSL.js';

function rightAnglePath(points, color, label, cableSpec) {
  const segments = [];
  for (let i = 0; i < points.length - 1; i++) {
    segments.push({
      start: points[i],
      end: points[i + 1],
      color,
      label,
      cableSpec,
    });
  }
  return segments;
}

const conductors = [
  ...rightAnglePath(
    [
      [-18, 7.5, 0], // DC Junction Box
      [-10, 7.5, 0], // Inverter
    ],
    '#dc2626',
    'DC+',
    'Cu 1C 4 sq mm',
  ),
  ...rightAnglePath(
    [
      [-28, 4, 0], // DC Junction Box
      [-19, 4, 0], // Inverter
    ],
    '#dc2626',
    'DC+',
    'Cu 1C 4 sq mm',
  ),
  ...rightAnglePath(
    [
      [-28, 4, 0],
      [-28, 7, 0],
    ],
    '#6b7280',
    'Neutral',
    'Cu 1C 6 sq mm',
  ),

  ...rightAnglePath(
    [
      [-19, 4, 0],
      [-19, 7, 0],
    ],
    '#6b7280',
    'Neutral',
    'Cu 1C 6 sq mm',
  ),

  ...rightAnglePath(
    [
      [-10 - 8, 8.5, 0], // DC Junction Box (lower)
      [-2 - 8, 8.5, 0], // Inverter (lower)
    ],
    '#1f2937',
    'DC-',
    'Cu 1C 4 sq mm',
  ),

  ...rightAnglePath(
    [
      [-2 - 8, 8, 0], // Inverter (lower)
      [6 - 8, 8, 0], // AC Box (lower)
    ],
    '#6b7280',
    'Neutral',
    'Cu 1C 6 sq mm',
  ),

  ...rightAnglePath(
    [
      [-15 - 8, 8, 0],
      [-10 - 8, 8, 0],
    ],
    '#dc2626',
    'DC+',
    'Cu 1C 4 sq mm',
  ),
  ...rightAnglePath(
    [
      [-15 - 8, 8, 0],
      [-10 - 8, 8, 0],
    ],
    '#dc2626',
    'DC+',
    'Cu 1C 4 sq mm',
  ),
  // Ac to solar meter
  ...rightAnglePath(
    [
      [-1, 8, 0],
      [15 - 8, 8, 0],
    ],
    '#6b7280',
    'Neutral',
    'Cu 1C 6 sq mm',
  ),

  ...rightAnglePath(
    [
      [7, 8, 0],
      [14, 8, 0],
    ],
    '#1d4ed8',
    'AC Line',
    'Cu 1C 6 sq mm',
  ),

  ...rightAnglePath(
    [
      [14, 8, 0],
      [21, 8, 0],
    ],
    '#1d4ed8',
    'AC Line',
    'Cu 1C 6 sq mm',
  ),

  ...rightAnglePath(
    [
      [19, 8, 0],
      [25.5, 8, 0],
    ],
    '#1d4ed8',
    'AC Line',
    'Cu 1C 16 sq mm',
  ),

  ...rightAnglePath(
    [
      [35 - 8, 8, 0],
      [44 - 8, 8, 0],
    ],
    '#1d4ed8',
    'AC Line',
    '2C Arm Al 10 sq mm',
  ),
  //vertical down
  ...rightAnglePath(
    [
      [45.2 - 8, 18, 0],
      [45.2 - 8, -5, 0],
    ],
    '#1d4ed8',
    'AC Line',
    '2C Arm Al 10 sq mm',
  ),
  //horizontal to LT panel
];

function Conductor({ conductor }) {
  const direction = [
    conductor.end[0] - conductor.start[0],
    // conductor.end[1] - conductor.start[1],
    conductor.end[2] - conductor.start[2],
  ];
  const length = Math.sqrt(
    direction[0] ** 2 + direction[1] ** 2 + direction[2] ** 2,
  );
  const midpoint = [
    (conductor.start[0] + conductor.end[0]) / 2,
    // (conductor.start[1] + conductor.end[1]) / 2,
    (conductor.start[2] + conductor.end[2]) / 2,
  ];

  return (
    <group>
      {/* Static wire base */}
      <mesh position={midpoint}>
        <cylinderGeometry args={[0.06, 0.06, length, 12]} />
        <meshLambertMaterial color={conductor.color} />
      </mesh>
      {/* Animated current flow */}
      <CurrentWire
        key={`current-${conductor.start.join('-')}-${conductor.end.join('-')}`}
        conductor={conductor}
      />
    </group>
  );
}

function EarthingRod({ position }) {
  return (
    <group>
      <mesh position={position}>
        <cylinderGeometry args={[0.12, 0.12, 17]} />
        <meshLambertMaterial color='#16a34a' />
      </mesh>
      <mesh
        position={[position[0], position[1] - 8, position[2]]}
        rotation={[-Math.PI / 2, 0, 0]}
      >
        <ringGeometry args={[0.6, 1, 12]} />
        <meshBasicMaterial color='#16a34a' side={2} />
      </mesh>
    </group>
  );
}

// Separate component for animated wires to prevent re-rendering
const AnimatedWires = React.memo(() => {
  return (
    <>
      {conductors.map((c, i) => (
        <Conductor key={`conductor-${i}`} conductor={c} />
      ))}
    </>
  );
});

const components = [
  {
    name: 'PV Array',
    position: [-18 - 8, 8, 0],
    size: [6, 3, 0.2],
    color: '#1e3a8a',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
  {
    name: 'DC Junction\nBox\n(IP56 rated)',
    position: [-18, 8, 0],
    size: [3.5, 3.5, 0.8],
    color: '#374151',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
  {
    name: 'Inverter',
    position: [-2 - 8, 8, 0],
    size: [5, 5, 1.5],
    color: '#059669',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
  {
    name: 'AC Box',
    position: [6 - 8, 8, 0],
    size: [3.5, 3.5, 0.8],
    color: 'black',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
  {
    name: 'Solar Meter',
    position: [6, 2, 0],
    size: [3.5, 3.5, 0.6],
    color: '#d97706',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
  {
    name: 'LoTo Box',
    position: [13, 8, 0],
    size: [3, 3, 0.8],
    color: '#dc2626',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
  {
    name: 'ELCB/ELR',
    position: [20, 8, 0],
    size: [2.5, 2.5, 0.8],
    color: '#b91c1c',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
  {
    name: 'LT Panel',
    position: [36 - 8, 8, 0],
    size: [7, 5, 0.8],
    color: 'transparent',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
  {
    name: 'Net Meter',
    position: [45 - 8, 2, 0, 8],
    size: [3.5, 3.5, 1.2],
    color: '#7c3aed',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
  {
    name: 'Grid & House Load',
    position: [45 - 8, 18, 0],
    size: [5, 3, 1.2],
    color: '#0891b2',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
  {
    name: 'House Load',
    position: [45 - 8, -5, 0],
    size: [5, 6, 1.2],
    color: '#0891b2',
    details: {
      Brand_name: '',
      Specification: '',
      Quantity: '',
    },
  },
];

function Scene({ onComponentHover, onComponentClick, componentsList }) {
  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 10, 5]} intensity={0.9} castShadow />

      {/* Animated wires - separate from hover state */}
      <AnimatedWires />

      {/* Components with hover and click events */}
      {componentsList.map((c, i) => (
        <ComponentBox
          key={i}
          component={c}
          onHover={onComponentHover}
          onClick={() => onComponentClick(c)}
        />
      ))}

      {/* Earthing rods: match SLD layout */}
      <EarthingRod position={[-24 - 8, -2, 0]} />
      <mesh position={[-24 - 8, 8.5, 0]}>
        <cylinderGeometry args={[0.2, 0.2, 4]} />
        <meshLambertMaterial color='black' />
      </mesh>
      <EarthingRod position={[-18 - 8, -2, 0.2]} />
      <group>
        {/* Rod from [-8, -2, 0] to [-8, -10, 0] */}
        <mesh position={[-14.1 - 8, -5, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.2, 0.2, 8, 16]} />
          <meshLambertMaterial color='#16a34a' />
        </mesh>
      </group>
      {/* Add a green rod from previous earthing to this one */}
      <group>
        {/* Rod from [-8, -2, 0] to [-8, -10, 0] */}
        <mesh position={[-10 - 8, 1, 0]}>
          <cylinderGeometry args={[0.12, 0.12, 12.2, 16]} />
          <meshLambertMaterial color='#16a34a' />
        </mesh>
      </group>

      {/* <EarthingRod position={[-8, -10, 0]} /> */}
      <EarthingRod position={[-2 - 8, -2, 0]} />

      {/* Add a green rod from previous earthing to this one */}
      <group>
        {/* Rod from [-8, -2, 0] to [-8, -10, 0] */}
        <mesh position={[-6, 2, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.2, 0.2, 7.8, 16]} />
          <meshLambertMaterial color='#16a34a' />
        </mesh>
      </group>
      <group>
        {/* Rod from [-8, -2, 0] to [-8, -10, 0] */}
        <mesh position={[-2, 4.4, 0]}>
          <cylinderGeometry args={[0.2, 0.2, 5, 16]} />
          <meshLambertMaterial color='#16a34a' />
        </mesh>
      </group>

      <OrbitControls
        enableRotate={true}
        minPolarAngle={Math.PI / 3} // 60° (90° = top view) → sets lower tilt
        maxPolarAngle={(2 * Math.PI) / 3} // 120° → sets upper tilt
        minAzimuthAngle={-Math.PI / 6} // -30° left
        maxAzimuthAngle={Math.PI / 6} // +30° right
        enableZoom={true}
        enablePan={true}
        minDistance={12} // Minimum distance from target (cannot zoom in closer than this)
        maxDistance={50}
      />
    </>
  );
}

export default function SolarPVDiagram({ onSelectComponent, materials }) {
  // Normalize materials: some saved payloads may be nested arrays (e.g. [[{...}, {...}]])
  const flattenedMaterials = Array.isArray(materials)
    ? materials.some(Array.isArray)
      ? materials.flat()
      : materials
    : [];

  // Map incoming materials to existing components by matching product name to
  // component name. If a material doesn't match any known component, discard
  // it (the user data may be for a different layout).
  const normalize = (s) =>
    (s || '')
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const findComponentIndexForProduct = (product) => {
    if (!product) return -1;
    const p = normalize(product);
    for (let i = 0; i < components.length; i++) {
      const compName = normalize(components[i].name);
      if (!compName) continue;
      // direct substring matches either way (covers short names like 'PV', 'Inverter')
      if (compName.includes(p) || p.includes(compName)) return i;
      // some product values might include key words: check for common words
      const keywords = [
        'pv',
        'array',
        'panel',
        'inverter',
        'meter',
        'panel',
        'lt',
        'load',
      ];
      for (const kw of keywords) {
        if (p.includes(kw) && compName.includes(kw)) return i;
      }
    }
    return -1;
  };

  let displayComponents = components.map((c) => ({
    ...c,
    details: { ...c.details },
  }));
  if (Array.isArray(flattenedMaterials) && flattenedMaterials.length > 0) {
    flattenedMaterials.forEach((m) => {
      const prod = m.product || m.productName || m.name || '';
      const idx = findComponentIndexForProduct(prod);
      if (idx !== -1) {
        displayComponents[idx] = {
          ...displayComponents[idx],
          name: displayComponents[idx].name,
          details: {
            Brand_name: m.brand || m.Brand_name || '',
            Specification: m.description || m.Specification || '',
            Quantity: m.quantity || m.Quantity || '',
          },
        };
      } else {
        // discard unmatched materials; they may not belong to this SLD layout
        // keep a console message for debugging
        // eslint-disable-next-line no-console
        console.warn('SolarPVDiagram: discarding unmatched material', prod);
      }
    });
  }

  const [hoveredComponent, setHoveredComponent] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Track mouse position for tooltip
  const handleMouseMove = (event) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  const handleComponentClick = (component) => {
    setSelectedComponent(component);
    onSelectComponent?.(component);
    console.log('Clicked component:', component);
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        background: 'linear-gradient(135deg, #e0e7ff 0%, #f5f7fa 100%)',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onMouseMove={handleMouseMove}
    >
      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        <Canvas
          camera={{ position: [0, 0, 10], fov: 130 }}
          shadows
          style={{
            background: 'white',
            color: 'black',
            width: '100%',
            height: '100%',
          }}
        >
          <Scene
            onComponentHover={setHoveredComponent}
            onComponentClick={handleComponentClick}
            componentsList={displayComponents}
          />
        </Canvas>

        {/* Tooltip */}
        {hoveredComponent && (
          <div
            style={{
              position: 'absolute',
              left: mousePosition.x - 166,
              top: mousePosition.y - 16,
              zIndex: 30,
              background: 'rgba(30, 41, 59, 0.98)',
              color: 'white',
              padding: '18px 24px',
              borderRadius: 12,
              fontSize: '1.1rem',
              fontWeight: 500,
              boxShadow: '0 4px 16px rgba(30, 60, 120, 0.12)',
              maxWidth: 320,
              whiteSpace: 'pre-line',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontWeight: 700 }}>{hoveredComponent}</div>
            <div style={{ fontSize: '0.95em', opacity: 0.85, marginTop: 4 }}>
              Brand name:{' '}
              {
                displayComponents.find((c) => c.name === hoveredComponent)
                  ?.details?.Brand_name
              }
              <br />
              Quantity:{' '}
              {
                displayComponents.find((c) => c.name === hoveredComponent)
                  ?.details?.Quantity
              }
              <br />
              Specification:{' '}
              {
                displayComponents.find((c) => c.name === hoveredComponent)
                  ?.details?.Specification
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
